import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IconLogin, IconCart, IconLogout, IconHeart } from "../../icons/Icons";
import "./StoreHeader.css";
import { useConfiguracionTiendaPublic } from "../../pages/store/hooks/useConfiguracionTiendaPublic";
import { validateClienteSession } from "../../services/LoginUserCliente";
import { getCategoriasPublic } from "../../services/public/CategoriasPublic";
import { obtenerSubcategoriasPublic } from "../../services/public/subcategoriasPublic";
import { getMarcasPublic } from "../../services/public/MarcasPublic";
import { getStoreHeaderBranding } from "../../services/public/Branding";
import { fileURL } from "../../utils/fileURL";
import { getCartCount, getCartTotal } from "../../utils/cart";
import { getFavoritesCount } from "../../utils/favorites";

const DEFAULT_HEADER_BRANDING = {
  nombre_tienda: "Cyravi",
  logo_url: "/image/cyravi-default-logo.png",
  placeholder_busqueda: "¿Qué estás buscando hoy?",
  texto_login: "Mi cuenta",
  texto_cuenta: "Mi cuenta",
  texto_logout: "Cerrar sesión",
  texto_carrito: "Carrito",
  navigation: [
    { key: "inicio", label: "Inicio", type: "link", url: "/store/inicio" },
    { key: "productos", label: "Explorar", type: "mega_menu" },
    { key: "marcas", label: "Marcas", type: "dropdown" },
    { key: "ofertas", label: "Ofertas", type: "link", url: "/store/productos" },
    {
      key: "destacados",
      label: "Destacados",
      type: "link",
      url: "/store/productos?destacados=1",
    },
  ],
  tema: {
    color_primario: "#1f2937",
    color_secundario: "#475569",

    color_texto_primario: "#0f172a",
    color_texto_secundario: "#475569",
    color_texto_terciario: "#64748b",
    color_texto_contraste: "#ffffff",
    color_texto_peligro: "#be123c",

    color_borde_primario: "rgba(148, 163, 184, 0.22)",
    color_borde_fuerte: "rgba(100, 116, 139, 0.26)",

    color_fondo_primario: "#ffffff",
    color_fondo_secundario: "#f8fafc",
    color_fondo_terciario: "#f1f5f9",
    color_fondo_cuaternario: "#eef2f7",
    color_fondo_quinario: "#e2e8f0",
    color_fondo_senario: "rgba(15, 23, 42, 0.04)",

    color_resplandor_izquierdo: "rgba(148, 163, 184, 0.18)",
    color_resplandor_derecho: "rgba(203, 213, 225, 0.16)",
    color_anillo_enfoque: "rgba(15, 23, 42, 0.12)",

    sombra_suave: "0 6px 18px rgba(15, 23, 42, 0.05)",
    sombra_principal: "0 12px 40px rgba(15, 23, 42, 0.08)",
    sombra_boton_primario: "0 4px 14px rgba(15, 23, 42, 0.12)",
    sombra_boton_primario_hover: "0 6px 18px rgba(15, 23, 42, 0.16)",
  },
};

function unwrapCategoriesResponse(res) {
  if (Array.isArray(res)) return res;
  if (res?.categorias && Array.isArray(res.categorias)) return res.categorias;
  if (res?.data && Array.isArray(res.data)) return res.data;
  return [];
}

function normalizeCategories(raw) {
  if (!Array.isArray(raw)) return [];

  return raw.map((cat) => ({
    categoria_producto_id: Number(cat?.categoria_producto_id),
    nombre: cat?.nombre || "Categoría",
    subcategorias: Array.isArray(cat?.subcategorias)
      ? cat.subcategorias.map((sub) => ({
          subcategoria_id: Number(sub?.subcategoria_id || sub?.subcategorias_id),
          nombre: sub?.nombre || "Subcategoría",
        }))
      : [],
    marcas: Array.isArray(cat?.marcas) ? cat.marcas : [],
  }));
}

/** La API pública suele devolver subcategorías en otro endpoint (lista plana). */
function enrichCategoriesWithFlatSubcategorias(categories, subsFlat) {
  if (!Array.isArray(categories) || categories.length === 0) return categories;
  if (!Array.isArray(subsFlat) || subsFlat.length === 0) return categories;

  const byCat = new Map();
  for (const s of subsFlat) {
    const cid = Number(
      s?.categoria_producto_id ?? s?.categoria_id ?? s?.categoria_producto
    );
    if (!Number.isFinite(cid)) continue;
    const sid = Number(s?.subcategoria_id ?? s?.subcategorias_id);
    if (!Number.isFinite(sid)) continue;
    if (!byCat.has(cid)) byCat.set(cid, []);
    byCat.get(cid).push({
      subcategoria_id: sid,
      nombre: String(s?.nombre ?? "").trim() || "Subcategoría",
    });
  }

  return categories.map((cat) => {
    const cid = Number(cat.categoria_producto_id);
    const fromNested = Array.isArray(cat.subcategorias) ? cat.subcategorias : [];
    if (fromNested.length > 0) return cat;
    const merged = byCat.get(cid);
    if (!merged?.length) return cat;
    return { ...cat, subcategorias: merged };
  });
}

function extractBrandsFromCategories(categories) {
  const map = new Map();

  categories.forEach((cat) => {
    if (!Array.isArray(cat?.marcas)) return;

    cat.marcas.forEach((marca) => {
      const id = Number(marca?.marca_producto_id);
      if (!Number.isFinite(id)) return;

      if (!map.has(id)) {
        map.set(id, {
          marca_producto_id: id,
          nombre: marca?.nombre || "Marca",
        });
      }
    });
  });

  return Array.from(map.values());
}

function unwrapMarcasResponse(res) {
  if (Array.isArray(res)) return res;
  if (res?.marcas && Array.isArray(res.marcas)) return res.marcas;
  if (res?.data && Array.isArray(res.data)) return res.data;
  return [];
}

function normalizePublicBrands(raw) {
  return unwrapMarcasResponse(raw)
    .map((m) => ({
      marca_producto_id: Number(m?.marca_producto_id ?? m?.marca_id ?? m?.id),
      nombre: String(m?.nombre ?? "").trim() || "Marca",
    }))
    .filter((m) => Number.isFinite(m.marca_producto_id));
}

function mergeBrandLists(...lists) {
  const map = new Map();
  for (const list of lists) {
    for (const b of list || []) {
      const id = Number(b?.marca_producto_id);
      if (!Number.isFinite(id)) continue;
      if (!map.has(id)) {
        map.set(id, {
          marca_producto_id: id,
          nombre: b?.nombre || "Marca",
        });
      }
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    a.nombre.localeCompare(b.nombre, "es")
  );
}

function StoreHeader({ onSearch, onReady }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Mantiene la última referencia de onReady sin re-disparar el fetch de branding.
  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [branding, setBranding] = useState(DEFAULT_HEADER_BRANDING);
  const [searchValue, setSearchValue] = useState("");
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [navOpen, setNavOpen] = useState(null);
  const [activeMegaCategory, setActiveMegaCategory] = useState(null);
  const [activeMenuBrand, setActiveMenuBrand] = useState(null);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [mobileExploreOpen, setMobileExploreOpen] = useState(false);
  const [mobileMarcasOpen, setMobileMarcasOpen] = useState(false);
  const [mobileCatOpenId, setMobileCatOpenId] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [favCount, setFavCount] = useState(0);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const { formatMoney } = useConfiguracionTiendaPublic();

  const navRef = useRef(null);
  const navCloseTimerRef = useRef(null);
  const accountRef = useRef(null);

  const clearNavCloseTimer = () => {
    if (navCloseTimerRef.current != null) {
      clearTimeout(navCloseTimerRef.current);
      navCloseTimerRef.current = null;
    }
  };

  const openNavPanel = (key) => {
    clearNavCloseTimer();
    setNavOpen(key);
  };

  const scheduleCloseNavPanel = () => {
    clearNavCloseTimer();
    navCloseTimerRef.current = window.setTimeout(() => {
      setNavOpen(null);
      navCloseTimerRef.current = null;
    }, 240);
  };

  let cliente = null;
  const token = localStorage.getItem("cliente_token");

  try {
    cliente = JSON.parse(localStorage.getItem("cliente"));
  } catch {
    cliente = null;
  }

  const isLogged = !!token && !!cliente;

  const mergedBranding = useMemo(() => {
    const b = branding || {};
    const loginRaw =
      typeof b.texto_login === "string" ? b.texto_login.trim() : "";
    const texto_login =
      loginRaw && loginRaw.toLowerCase() !== "login"
        ? loginRaw
        : DEFAULT_HEADER_BRANDING.texto_login;

    const logoutRaw =
      typeof b.texto_logout === "string" ? b.texto_logout.trim() : "";
    const legacyLogout = new Set(["logout", "cerrar sesión"]);
    const texto_logout =
      logoutRaw && !legacyLogout.has(logoutRaw.toLowerCase())
        ? logoutRaw
        : DEFAULT_HEADER_BRANDING.texto_logout;

    return {
      ...DEFAULT_HEADER_BRANDING,
      ...b,
      texto_login,
      texto_logout,
      tema: {
        ...DEFAULT_HEADER_BRANDING.tema,
        ...(b.tema || {}),
      },
      navigation: Array.isArray(b.navigation)
        ? b.navigation.map((item) => {
            if (item?.type === "link" && item?.url === "/store") {
              return { ...item, url: "/store/inicio" };
            }
            return item;
          })
        : DEFAULT_HEADER_BRANDING.navigation,
    };
  }, [branding]);

  const logoSrc = useMemo(() => {
    return fileURL(mergedBranding.logo_url) || DEFAULT_HEADER_BRANDING.logo_url;
  }, [mergedBranding.logo_url]);

  const cuentaLabel = useMemo(() => {
    return isLogged
      ? mergedBranding.texto_cuenta || "Mi cuenta"
      : mergedBranding.texto_login || "Mi cuenta";
  }, [isLogged, mergedBranding.texto_cuenta, mergedBranding.texto_login]);

  // Nombre corto del cliente para la 2.ª línea de la acción "cuenta".
  const clienteNombre = useMemo(() => {
    if (!isLogged || !cliente) return "";
    const raw =
      cliente.nombres ||
      cliente.nombre ||
      cliente.nombre_completo ||
      cliente.correo ||
      cliente.email ||
      "";
    const first = String(raw).trim().split(/\s+/)[0] || "";
    return first.length > 14 ? `${first.slice(0, 13)}…` : first;
  }, [isLogged, cliente]);

  // Línea principal (negrita) de la acción cuenta.
  const cuentaMainLabel = isLogged
    ? clienteNombre || cuentaLabel
    : "Iniciar sesión";

  // Detecta qué ítem de navegación corresponde a la ruta actual, para
  // resaltarlo (referencia visual de la sección seleccionada).
  const activeNavKey = useMemo(() => {
    const path = location.pathname || "";
    const params = new URLSearchParams(location.search || "");
    if (path.startsWith("/store/inicio")) return "inicio";
    if (path.startsWith("/store/productos")) {
      const oferta = String(
        params.get("oferta") ?? params.get("ofertas") ?? ""
      ).toLowerCase();
      const destacados = String(
        params.get("destacados") ?? params.get("destacado") ?? ""
      ).toLowerCase();
      if (destacados === "1" || destacados === "true") return "destacados";
      if (oferta === "1" || oferta === "true") return "ofertas";
      if (params.get("brand")) return "marcas";
      return "productos";
    }
    return null;
  }, [location.pathname, location.search]);

  const themeVars = useMemo(() => {
    const tema = mergedBranding.tema || {};

    return {
      "--sh-color-primario":
        tema.color_primario || DEFAULT_HEADER_BRANDING.tema.color_primario,
      "--sh-color-secundario":
        tema.color_secundario || DEFAULT_HEADER_BRANDING.tema.color_secundario,

      "--sh-color-texto-primario":
        tema.color_texto_primario ||
        DEFAULT_HEADER_BRANDING.tema.color_texto_primario,
      "--sh-color-texto-secundario":
        tema.color_texto_secundario ||
        DEFAULT_HEADER_BRANDING.tema.color_texto_secundario,
      "--sh-color-texto-terciario":
        tema.color_texto_terciario ||
        DEFAULT_HEADER_BRANDING.tema.color_texto_terciario,
      "--sh-color-texto-contraste":
        tema.color_texto_contraste ||
        DEFAULT_HEADER_BRANDING.tema.color_texto_contraste,
      "--sh-color-peligro":
        tema.color_texto_peligro ||
        DEFAULT_HEADER_BRANDING.tema.color_texto_peligro,

      "--sh-color-borde-primario":
        tema.color_borde_primario ||
        DEFAULT_HEADER_BRANDING.tema.color_borde_primario,
      "--sh-color-borde-fuerte":
        tema.color_borde_fuerte ||
        DEFAULT_HEADER_BRANDING.tema.color_borde_fuerte,

      "--sh-color-fondo-primario":
        tema.color_fondo_primario ||
        DEFAULT_HEADER_BRANDING.tema.color_fondo_primario,
      "--sh-color-fondo-secundario":
        tema.color_fondo_secundario ||
        DEFAULT_HEADER_BRANDING.tema.color_fondo_secundario,
      "--sh-color-fondo-terciario":
        tema.color_fondo_terciario ||
        DEFAULT_HEADER_BRANDING.tema.color_fondo_terciario,
      "--sh-color-fondo-cuaternario":
        tema.color_fondo_cuaternario ||
        DEFAULT_HEADER_BRANDING.tema.color_fondo_cuaternario,
      "--sh-color-fondo-quinario":
        tema.color_fondo_quinario ||
        DEFAULT_HEADER_BRANDING.tema.color_fondo_quinario,
      "--sh-color-fondo-senario":
        tema.color_fondo_senario ||
        DEFAULT_HEADER_BRANDING.tema.color_fondo_senario,

      "--sh-color-resplandor-izquierdo":
        tema.color_resplandor_izquierdo ||
        DEFAULT_HEADER_BRANDING.tema.color_resplandor_izquierdo,
      "--sh-color-resplandor-derecho":
        tema.color_resplandor_derecho ||
        DEFAULT_HEADER_BRANDING.tema.color_resplandor_derecho,
      "--sh-color-anillo-enfoque":
        tema.color_anillo_enfoque ||
        DEFAULT_HEADER_BRANDING.tema.color_anillo_enfoque,

      "--sh-sombra-suave":
        tema.sombra_suave || DEFAULT_HEADER_BRANDING.tema.sombra_suave,
      "--sh-sombra-principal":
        tema.sombra_principal || DEFAULT_HEADER_BRANDING.tema.sombra_principal,
      "--sh-sombra-boton-primario":
        tema.sombra_boton_primario ||
        DEFAULT_HEADER_BRANDING.tema.sombra_boton_primario,
      "--sh-sombra-boton-primario-hover":
        tema.sombra_boton_primario_hover ||
        DEFAULT_HEADER_BRANDING.tema.sombra_boton_primario_hover,
    };
  }, [mergedBranding]);

  const selectedBrandFromRoute = useMemo(() => {
    const path = String(location.pathname || "");
    if (!path.includes("/store/productos")) {
      return null;
    }
    try {
      const qs = new URLSearchParams(location.search || "");
      const raw = qs.get("brand");
      if (raw == null || raw === "") return null;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  }, [location.pathname, location.search]);

  const handleLoginClick = () => {
    setMobilePanelOpen(false);

    if (isLogged) {
      navigate("/cliente");
    } else {
      navigate("/cliente/login", {
        state: { mode: "login", redirectTo: "/cliente" },
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("cliente_token");
    localStorage.removeItem("cliente");
    setMobilePanelOpen(false);
    navigate("/store/inicio", { replace: true });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearch?.(value);
  };

  const handleLogoError = (e) => {
    if (e.currentTarget.dataset.fallbackApplied === "1") return;
    e.currentTarget.dataset.fallbackApplied = "1";
    e.currentTarget.src = DEFAULT_HEADER_BRANDING.logo_url;
  };

  const closeAllMenus = () => {
    clearNavCloseTimer();
    setNavOpen(null);
    setMobilePanelOpen(false);
  };

  const goToCategory = (categoryId) => {
    onSearch?.("");
    setSearchValue("");
    const params = new URLSearchParams(location.search || "");
    params.set("category", String(categoryId));
    params.delete("subcategory");
    params.delete("brand");

    navigate({
      pathname: "/store/productos",
      search: params.toString(),
    });

    closeAllMenus();
  };

  const goToSubcategory = (categoryId, subcategoryId) => {
    onSearch?.("");
    setSearchValue("");
    const params = new URLSearchParams();
    params.set("category", String(categoryId));
    params.set("subcategory", String(subcategoryId));

    navigate({
      pathname: "/store/productos",
      search: params.toString(),
    });

    closeAllMenus();
  };

  const goToBrand = (brandId) => {
    onSearch?.("");
    setSearchValue("");
    const params = new URLSearchParams();
    params.set("brand", String(brandId));

    navigate({
      pathname: "/store/productos",
      search: params.toString(),
    });

    closeAllMenus();
  };

  const goToAllProducts = () => {
    onSearch?.("");
    setSearchValue("");
    navigate({ pathname: "/store/productos", search: "" });
    closeAllMenus();
  };

  const goToOfertas = () => {
    onSearch?.("");
    setSearchValue("");
    navigate({ pathname: "/store/productos", search: "?oferta=1" });
    closeAllMenus();
  };

  useEffect(() => {
    if (navOpen !== "productos") return;
    if (categories.length === 0) {
      setActiveMegaCategory(null);
      return;
    }
    setActiveMegaCategory((prev) => {
      if (
        prev &&
        categories.some(
          (c) => c.categoria_producto_id === prev.categoria_producto_id
        )
      ) {
        return prev;
      }
      return categories[0];
    });
  }, [navOpen, categories]);

  useEffect(() => {
    if (navOpen !== "marcas") return;
    if (brands.length === 0) {
      setActiveMenuBrand(null);
      return;
    }
    setActiveMenuBrand((prev) => {
      if (selectedBrandFromRoute != null) {
        const fromRoute = brands.find(
          (b) => b.marca_producto_id === selectedBrandFromRoute
        );
        if (fromRoute) return fromRoute;
      }
      if (
        prev &&
        brands.some((b) => b.marca_producto_id === prev.marca_producto_id)
      ) {
        return prev;
      }
      return brands[0];
    });
  }, [navOpen, brands, selectedBrandFromRoute]);

  useEffect(() => {
    let cancelled = false;
    let nombreTienda = DEFAULT_HEADER_BRANDING.nombre_tienda;

    (async () => {
      try {
        const res = await getStoreHeaderBranding();
        if (cancelled) return;

        const data = res?.data || {};
        nombreTienda =
          String(data.nombre_tienda ?? "").trim() || nombreTienda;

        setBranding({
          ...DEFAULT_HEADER_BRANDING,
          ...data,
          tema: {
            ...DEFAULT_HEADER_BRANDING.tema,
            ...(data.tema || {}),
          },
          navigation: Array.isArray(data?.navigation)
            ? data.navigation
            : DEFAULT_HEADER_BRANDING.navigation,
        });
      } catch {
        if (!cancelled) {
          setBranding(DEFAULT_HEADER_BRANDING);
        }
      } finally {
        // Avisa al layout que el branding ya se resolvió (para quitar el splash).
        if (!cancelled) onReadyRef.current?.({ nombreTienda });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [catRes, subsFlat, marcasRes] = await Promise.all([
          getCategoriasPublic(),
          obtenerSubcategoriasPublic(),
          getMarcasPublic(),
        ]);
        if (cancelled) return;

        const rawList = unwrapCategoriesResponse(catRes);
        const normalized = normalizeCategories(rawList);
        const withSubs = enrichCategoriesWithFlatSubcategorias(
          normalized,
          Array.isArray(subsFlat) ? subsFlat : []
        );
        setCategories(withSubs);
        setBrands(
          mergeBrandLists(
            normalizePublicBrands(marcasRes),
            extractBrandsFromCategories(withSubs)
          )
        );
        setActiveMegaCategory(withSubs[0] || null);
      } catch (err) {
        console.error("Error loading categories:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isLogged) return;

    let cancelled = false;

    (async () => {
      const r = await validateClienteSession();
      if (cancelled) return;

      if (!r?.ok) {
        localStorage.removeItem("cliente_token");
        localStorage.removeItem("cliente");

        navigate("/cliente/login", {
          replace: true,
          state: {
            mode: "login",
            reason: r?.reason || "expired_or_invalid",
            message: "Tu sesión expiró o no es válida. Inicia sesión de nuevo.",
          },
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, isLogged, navigate]);

  useEffect(() => {
    const onScroll = () => setIsHeaderScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const syncCart = () => {
      try {
        setCartCount(getCartCount());
        setCartTotal(getCartTotal());
      } catch {
        setCartCount(0);
        setCartTotal(0);
      }
    };

    syncCart();
    window.addEventListener("cart:updated", syncCart);
    window.addEventListener("storage", syncCart);

    return () => {
      window.removeEventListener("cart:updated", syncCart);
      window.removeEventListener("storage", syncCart);
    };
  }, []);

  useEffect(() => {
    const syncFav = () => {
      try {
        setFavCount(getFavoritesCount());
      } catch {
        setFavCount(0);
      }
    };

    syncFav();
    window.addEventListener("favorites:updated", syncFav);
    window.addEventListener("storage", syncFav);

    return () => {
      window.removeEventListener("favorites:updated", syncFav);
      window.removeEventListener("storage", syncFav);
    };
  }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      if (!navRef.current) return;
      if (!navRef.current.contains(e.target)) {
        clearNavCloseTimer();
        setNavOpen(null);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(
    () => () => {
      if (navCloseTimerRef.current != null) {
        clearTimeout(navCloseTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    clearNavCloseTimer();
    setNavOpen(null);
    setMobilePanelOpen(false);
    setAccountMenuOpen(false);
  }, [location.pathname, location.search]);

  // Cierra el submenú de cuenta al hacer clic fuera o con Escape.
  useEffect(() => {
    if (!accountMenuOpen) return;
    const onDocMouseDown = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setAccountMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [accountMenuOpen]);

  useEffect(() => {
    if (!mobilePanelOpen) {
      setMobileExploreOpen(false);
      setMobileMarcasOpen(false);
      setMobileCatOpenId(null);
    }
  }, [mobilePanelOpen]);

  return (
    <div className="cyravi-StoreHeader" style={themeVars}>
      <header className={`cyravi-fh ${isHeaderScrolled ? "is-scrolled" : ""}`}>
        <div className="cyravi-fh__stack">
          <div className="cyravi-fh__primary">
            <div className="cyravi-fh__primary-inner">
              <div className="cyravi-fh__row cyravi-fh__row--top">
                <button
                  type="button"
                  className="cyravi-fh__brand"
                  onClick={() => navigate("/store/inicio")}
                  aria-label="Ir al inicio de la tienda"
                >
                  <img
                    src={logoSrc}
                    className="cyravi-fh__logo"
                    alt={
                      mergedBranding.nombre_tienda ||
                      DEFAULT_HEADER_BRANDING.nombre_tienda
                    }
                    onError={handleLogoError}
                  />
                </button>

                <div className="cyravi-fh__search-block">
                  <div className="cyravi-fh__search-shell">
                    <span className="cyravi-fh__search-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7" />
                        <path d="M20 20l-3.6-3.6" />
                      </svg>
                    </span>
                    <input
                      className="cyravi-fh__search-input"
                      placeholder={
                        mergedBranding.placeholder_busqueda ||
                        DEFAULT_HEADER_BRANDING.placeholder_busqueda
                      }
                      value={searchValue}
                      onChange={handleSearchChange}
                    />
                  </div>
                </div>

                <div className="cyravi-fh__actions">
                  <div className="cyravi-fh__account" ref={accountRef}>
                    <button
                      type="button"
                      className={`cyravi-fh__action-btn ${
                        isLogged && accountMenuOpen ? "is-active" : ""
                      }`}
                      onClick={() =>
                        isLogged
                          ? setAccountMenuOpen((prev) => !prev)
                          : handleLoginClick()
                      }
                      aria-label={cuentaLabel}
                      aria-haspopup={isLogged ? "menu" : undefined}
                      aria-expanded={isLogged ? accountMenuOpen : undefined}
                    >
                      <span className="cyravi-fh__action-ico">{IconLogin}</span>
                      <span className="cyravi-fh__action-text">
                        <span className="cyravi-fh__action-top">{cuentaLabel}</span>
                        <span className="cyravi-fh__action-main">
                          {cuentaMainLabel}
                        </span>
                      </span>
                    </button>

                    {isLogged && accountMenuOpen && (
                      <div className="cyravi-fh__account-menu" role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          className="cyravi-fh__account-item"
                          onClick={() => {
                            setAccountMenuOpen(false);
                            navigate("/cliente");
                          }}
                        >
                          {IconLogin}
                          <span>Ingresar</span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="cyravi-fh__account-item cyravi-fh__account-item--danger"
                          onClick={() => {
                            setAccountMenuOpen(false);
                            handleLogout();
                          }}
                        >
                          {IconLogout}
                          <span>Logout</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="cyravi-fh__action-btn cyravi-fh__fav-btn"
                    onClick={() => navigate("/store/favoritos")}
                    aria-label={`Favoritos${
                      favCount > 0 ? ` (${favCount})` : ""
                    }`}
                  >
                    <span className="cyravi-fh__action-ico">
                      {IconHeart}
                      {favCount > 0 && (
                        <span className="cyravi-fh__cart-count" aria-hidden="true">
                          {favCount > 99 ? "99+" : favCount}
                        </span>
                      )}
                    </span>
                    <span className="cyravi-fh__action-text">
                      <span className="cyravi-fh__action-top">Mi lista</span>
                      <span className="cyravi-fh__action-main">Favoritos</span>
                    </span>
                  </button>

                  <button
                    type="button"
                    className="cyravi-fh__action-btn cyravi-fh__cart-btn"
                    onClick={() => navigate("/store/cart")}
                    aria-label={`${mergedBranding.texto_carrito || "Carrito"}${
                      cartCount > 0 ? ` (${cartCount})` : ""
                    }`}
                  >
                    <span className="cyravi-fh__action-ico">
                      {IconCart}
                      {cartCount > 0 && (
                        <span className="cyravi-fh__cart-count" aria-hidden="true">
                          {cartCount > 99 ? "99+" : cartCount}
                        </span>
                      )}
                    </span>
                    <span className="cyravi-fh__action-text">
                      <span className="cyravi-fh__action-top">
                        {mergedBranding.texto_carrito || "Carrito"}
                      </span>
                      <span className="cyravi-fh__action-main">
                        {formatMoney(cartTotal)}
                      </span>
                    </span>
                  </button>

                  <button
                    type="button"
                    className={`cyravi-fh__menu-btn ${
                      mobilePanelOpen ? "is-active" : ""
                    }`}
                    onClick={() => setMobilePanelOpen((prev) => !prev)}
                    aria-label="Abrir menú"
                  >
                    <span />
                    <span />
                    <span />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="cyravi-fh__nav-strip">
            <div className="cyravi-fh__nav-row" ref={navRef}>
                <nav className="cyravi-fh__nav" aria-label="Principal">
                {mergedBranding.navigation.map((item) => {
                if (item.type === "link") {
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`cyravi-fh__nav-link ${
                        activeNavKey === item.key ? "is-active" : ""
                      }`}
                      onClick={() =>
                        item.key === "ofertas"
                          ? goToOfertas()
                          : navigate(item.url || "/store/inicio")
                      }
                    >
                      {item.label}
                    </button>
                  );
                }

                if (item.key === "productos") {
                  return (
                    <div
                      key={item.key}
                      className="cyravi-fh__nav-item"
                      onMouseEnter={() => openNavPanel("productos")}
                      onMouseLeave={scheduleCloseNavPanel}
                    >
                      <button
                        type="button"
                        className={`cyravi-fh__nav-link ${
                          navOpen === "productos" || activeNavKey === "productos"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() => {
                          clearNavCloseTimer();
                          setNavOpen((prev) =>
                            prev === "productos" ? null : "productos"
                          );
                        }}
                      >
                        {item.label}
                      </button>

                      {navOpen === "productos" && (
                        <div className="cyravi-fh__mega-menu cyravi-fh__mega-menu--explore">
                          <div className="cyravi-fh__mega-explore-grid">
                            <div className="cyravi-fh__mega-explore-pair">
                              <div className="cyravi-fh__mega-col cyravi-fh__mega-col--categories">
                                <h4 className="cyravi-fh__mega-title">
                                  Categorías
                                </h4>

                                {categories.length === 0 ? (
                                  <div className="cyravi-fh__mega-empty">
                                    No hay categorías disponibles
                                  </div>
                                ) : (
                                  <div className="cyravi-fh__mega-scroll">
                                    {categories.map((cat) => (
                                      <button
                                        key={cat.categoria_producto_id}
                                        type="button"
                                        className={`cyravi-fh__mega-category ${
                                          activeMegaCategory?.categoria_producto_id ===
                                          cat.categoria_producto_id
                                            ? "is-active"
                                            : ""
                                        }`}
                                        onMouseEnter={() =>
                                          setActiveMegaCategory(cat)
                                        }
                                        onClick={() =>
                                          goToCategory(cat.categoria_producto_id)
                                        }
                                      >
                                        {cat.nombre}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div className="cyravi-fh__mega-col cyravi-fh__mega-col--subcategories">
                                <h4 className="cyravi-fh__mega-title">
                                  Subcategorías
                                </h4>
                                {!activeMegaCategory ? (
                                  <div className="cyravi-fh__mega-empty">
                                    Elige una categoría a la izquierda.
                                  </div>
                                ) : activeMegaCategory.subcategorias?.length >
                                  0 ? (
                                  <div className="cyravi-fh__mega-scroll">
                                    {activeMegaCategory.subcategorias.map(
                                      (sub) => (
                                        <button
                                          key={sub.subcategoria_id}
                                          type="button"
                                          className="cyravi-fh__mega-subcategory"
                                          onClick={() =>
                                            goToSubcategory(
                                              activeMegaCategory.categoria_producto_id,
                                              sub.subcategoria_id
                                            )
                                          }
                                        >
                                          {sub.nombre}
                                        </button>
                                      )
                                    )}
                                  </div>
                                ) : (
                                  <div className="cyravi-fh__mega-empty">
                                    No se encontraron mas productos
                                    relacionados, puedes ver todos los productos
                                    de esta categoría.
                                  </div>
                                )}
                              </div>
                            </div>

                            <aside className="cyravi-fh__mega-aside">
                              <p className="cyravi-fh__mega-kicker">Explorar</p>
                              <h3 className="cyravi-fh__mega-heading">
                                Catálogo de productos
                              </h3>
                              <p className="cyravi-fh__mega-text">
                                Navega por categoría y subcategoría desde el
                                menú, o abre el listado completo sin filtros.
                              </p>
                              {activeMegaCategory && (
                                <p className="cyravi-fh__mega-aside-meta">
                                  Vista previa:{" "}
                                  <strong>{activeMegaCategory.nombre}</strong>
                                </p>
                              )}
                              <button
                                type="button"
                                className="cyravi-fh__mega-cta"
                                onClick={goToAllProducts}
                              >
                                Ver todos los productos
                              </button>
                            </aside>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                if (item.key === "marcas") {
                  return (
                    <div
                      key={item.key}
                      className="cyravi-fh__nav-item"
                      onMouseEnter={() => openNavPanel("marcas")}
                      onMouseLeave={scheduleCloseNavPanel}
                    >
                      <button
                        type="button"
                        className={`cyravi-fh__nav-link ${
                          navOpen === "marcas" || activeNavKey === "marcas"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() => {
                          clearNavCloseTimer();
                          setNavOpen((prev) =>
                            prev === "marcas" ? null : "marcas"
                          );
                        }}
                      >
                        {item.label}
                      </button>

                      {navOpen === "marcas" && (
                        <div className="cyravi-fh__mega-menu cyravi-fh__mega-menu--brands">
                          <div className="cyravi-fh__mega-brands-grid">
                            <div className="cyravi-fh__mega-brands-col">
                              <h4 className="cyravi-fh__mega-title">Marcas</h4>
                              {brands.length > 0 ? (
                                <div className="cyravi-fh__mega-scroll">
                                  {brands.map((brand) => (
                                    <button
                                      key={brand.marca_producto_id}
                                      type="button"
                                      className={`cyravi-fh__mega-category${
                                        activeMenuBrand?.marca_producto_id ===
                                        brand.marca_producto_id
                                          ? " is-active"
                                          : ""
                                      }`}
                                      onMouseEnter={() =>
                                        setActiveMenuBrand(brand)
                                      }
                                      onClick={() =>
                                        goToBrand(brand.marca_producto_id)
                                      }
                                    >
                                      {brand.nombre}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <div className="cyravi-fh__mega-empty">
                                  No hay marcas disponibles
                                </div>
                              )}
                            </div>
                            <aside className="cyravi-fh__mega-aside">
                              <p className="cyravi-fh__mega-kicker">Marcas</p>
                              <h3 className="cyravi-fh__mega-heading">
                                Productos por fabricante
                              </h3>
                              <p className="cyravi-fh__mega-text">
                                Elegí una marca para ver solo sus productos, o abrí el
                                catálogo completo sin filtro.
                              </p>
                              {activeMenuBrand && (
                                <p className="cyravi-fh__mega-aside-meta">
                                  Vista previa:{" "}
                                  <strong>{activeMenuBrand.nombre}</strong>
                                </p>
                              )}
                              <button
                                type="button"
                                className="cyravi-fh__mega-cta"
                                onClick={goToAllProducts}
                              >
                                Ver todos los productos
                              </button>
                            </aside>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`cyravi-fh__nav-link ${
                      activeNavKey === item.key ? "is-active" : ""
                    }`}
                    onClick={() => navigate(item.url || "/store/inicio")}
                  >
                    {item.label}
                  </button>
                );
              })}
                </nav>
              </div>
            </div>

          <div
            className={`cyravi-fh__mobile-panel ${
              mobilePanelOpen ? "is-open" : ""
            }`}
          >
            <div className="cyravi-fh__mobile-panel-inner">
              <div className="cyravi-fh__mobile-actions">
                {mergedBranding.navigation.map((item) => {
                  const closeAndGo = (url) => {
                    setMobilePanelOpen(false);
                    navigate(url || "/store/inicio");
                  };

                  if (item.type === "link") {
                    return (
                      <button
                        key={item.key}
                        type="button"
                        className="cyravi-fh__mobile-link"
                        onClick={() => {
                          if (item.key === "ofertas") {
                            setMobilePanelOpen(false);
                            goToOfertas();
                          } else {
                            closeAndGo(item.url);
                          }
                        }}
                      >
                        {item.label}
                      </button>
                    );
                  }

                  if (item.type === "mega_menu" && item.key === "productos") {
                    return (
                      <div key={item.key} className="cyravi-fh__mobile-accordion">
                        <button
                          type="button"
                          className="cyravi-fh__mobile-accordion-toggle"
                          onClick={() => {
                            setMobileExploreOpen((o) => !o);
                            setMobileMarcasOpen(false);
                          }}
                          aria-expanded={mobileExploreOpen}
                        >
                          <span>{item.label}</span>
                          <span
                            className={`cyravi-fh__mobile-accordion-chevron ${
                              mobileExploreOpen ? "is-open" : ""
                            }`}
                            aria-hidden
                          />
                        </button>
                        {mobileExploreOpen && (
                          <div
                            className="cyravi-fh__mobile-accordion-panel"
                            role="region"
                          >
                            {categories.length === 0 ? (
                              <p className="cyravi-fh__mobile-accordion-hint">
                                No hay categorías disponibles
                              </p>
                            ) : (
                              categories.map((cat) => {
                                const subs = Array.isArray(cat.subcategorias)
                                  ? cat.subcategorias
                                  : [];
                                const hasSubs = subs.length > 0;
                                const open =
                                  mobileCatOpenId === cat.categoria_producto_id;
                                return (
                                  <div
                                    key={cat.categoria_producto_id}
                                    className="cyravi-fh__mobile-tree"
                                  >
                                    <div className="cyravi-fh__mobile-tree-row">
                                      <button
                                        type="button"
                                        className="cyravi-fh__mobile-tree-main"
                                        onClick={() =>
                                          goToCategory(cat.categoria_producto_id)
                                        }
                                      >
                                        {cat.nombre}
                                      </button>
                                      {hasSubs && (
                                        <button
                                          type="button"
                                          className="cyravi-fh__mobile-tree-more"
                                          aria-expanded={open}
                                          aria-label="Subcategorías"
                                          onClick={() =>
                                            setMobileCatOpenId((id) =>
                                              id === cat.categoria_producto_id
                                                ? null
                                                : cat.categoria_producto_id
                                            )
                                          }
                                        >
                                          ▾
                                        </button>
                                      )}
                                    </div>
                                    {hasSubs && open && (
                                      <div className="cyravi-fh__mobile-tree-subs">
                                        {subs.map((sub) => (
                                          <button
                                            type="button"
                                            key={sub.subcategoria_id}
                                            className="cyravi-fh__mobile-tree-sub"
                                            onClick={() =>
                                              goToSubcategory(
                                                cat.categoria_producto_id,
                                                sub.subcategoria_id
                                              )
                                            }
                                          >
                                            {sub.nombre}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                            <button
                              type="button"
                              className="cyravi-fh__mobile-all-products"
                              onClick={() => goToAllProducts()}
                            >
                              Ver todos los productos
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (item.type === "dropdown" && item.key === "marcas") {
                    return (
                      <div key={item.key} className="cyravi-fh__mobile-accordion">
                        <button
                          type="button"
                          className="cyravi-fh__mobile-accordion-toggle"
                          onClick={() => {
                            setMobileMarcasOpen((o) => !o);
                            setMobileExploreOpen(false);
                          }}
                          aria-expanded={mobileMarcasOpen}
                        >
                          <span>{item.label}</span>
                          <span
                            className={`cyravi-fh__mobile-accordion-chevron ${
                              mobileMarcasOpen ? "is-open" : ""
                            }`}
                            aria-hidden
                          />
                        </button>
                        {mobileMarcasOpen && (
                          <div
                            className="cyravi-fh__mobile-accordion-panel"
                            role="region"
                          >
                            {brands.length === 0 ? (
                              <p className="cyravi-fh__mobile-accordion-hint">
                                No hay marcas disponibles
                              </p>
                            ) : (
                              brands.map((brand) => (
                                <button
                                  type="button"
                                  key={brand.marca_producto_id}
                                  className="cyravi-fh__mobile-tree-sub"
                                  onClick={() =>
                                    goToBrand(brand.marca_producto_id)
                                  }
                                >
                                  {brand.nombre}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return null;
                })}

                <button
                  type="button"
                  className="cyravi-fh__mobile-link"
                  onClick={handleLoginClick}
                >
                  {cuentaLabel}
                </button>

                <button
                  type="button"
                  className="cyravi-fh__mobile-link"
                  onClick={() => {
                    setMobilePanelOpen(false);
                    navigate("/store/favoritos");
                  }}
                >
                  Favoritos
                  {favCount > 0 ? ` (${favCount > 99 ? "99+" : favCount})` : ""}
                </button>

                <button
                  type="button"
                  className="cyravi-fh__mobile-link"
                  onClick={() => {
                    setMobilePanelOpen(false);
                    navigate("/store/cart");
                  }}
                >
                  {mergedBranding.texto_carrito || "Carrito"}
                  {cartCount > 0 ? ` (${cartCount > 99 ? "99+" : cartCount})` : ""}
                </button>
              </div>

              {isLogged && (
                <button
                  type="button"
                  className="cyravi-fh__mobile-logout"
                  onClick={handleLogout}
                >
                  {mergedBranding.texto_logout ||
                    DEFAULT_HEADER_BRANDING.texto_logout}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
    </div>
  );
}

export default StoreHeader;